import { Injectable, Injector, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Common } from '@app/utils/common';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TimeoutCheckService implements OnDestroy {
  private readonly timeoutMinutes = 10; // 10 minute timeout
  private readonly warningTimeoutMinutes = 30; // 30 minutes warning timeout
  private timer: ReturnType<typeof setTimeout> | null = null;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();

  private readonly events = [
    'mousemove',
    'keydown',
    'wheel',
    'click',
    'touchstart',
    'touchmove',
    'scroll',
  ];

  constructor(private readonly router: Router, private readonly ngZone: NgZone, private readonly injector: Injector
  ) { }

  startTimer(): void {
    this.resetTimer();
    this.ngZone.runOutsideAngular(() => {
      this.events.forEach((e) => {
        globalThis.addEventListener(e, () => {
          this.resetTimer();
        });
      });
    });
  }

  stopTimer(): void {
    this.destroy$.next();
    if (this.timer) clearTimeout(this.timer);
    if (this.warningTimer) clearTimeout(this.warningTimer);
    this.events.forEach((e) => {
      globalThis.removeEventListener(e, () => this.resetTimer());
    });
  }

  private resetTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    if (this.warningTimer) clearTimeout(this.warningTimer);

    this.timer = globalThis.setTimeout(() => {
      this.ngZone.run(() => {
        this.showWarning();
      });
    }, this.timeoutMinutes * 60 * 1000);
  }

  private showWarning(): void {
    const toast = document.createElement('div');
    // Setting up a timer for automatic logout
    this.warningTimer = globalThis.setTimeout(() => {
      this.ngZone.run(async () => {
        modal.remove();
        this.stopTimer();
        Common.logout(this.router);
      });
    }, this.warningTimeoutMinutes * 60 * 1000);

    // Show warnings using non-blocking
    toast.style.cssText = `
        display: block;
        align-items: center;
        border-radius: 4px;
        justify-content: space-between;
        min-width: 400px;
        min-height: 110px;
        color: #000;
        padding: 10px 30px;
        background: #fff0cf;`;

    const titleContainer = document.createElement('div');
    titleContainer.style.cssText = `
        display: flex;
        align-items: center;
        margin-top: 10px;`;

    const iconDiv = document.createElement('div');
    const iconImg = document.createElement('img');
    iconImg.style.cssText = `  align-items: center;
        justify-content: center;
        border-radius: 50%;
        font-size: 40px;
        color: #ffaf00;`;
    iconImg.src = '/assets/images/warning.png';
    iconImg.alt = 'info icon';
    iconDiv.appendChild(iconImg);

    const titleDiv = document.createElement('div');
    titleDiv.style.cssText = `
          font-size: 20px;
          font-weight: 600;
          margin-left: 15px;`;
    titleDiv.textContent = 'Session Timeout';

    titleContainer.appendChild(iconDiv);
    titleContainer.appendChild(titleDiv);
    toast.appendChild(titleContainer);

    const messageDiv = document.createElement('div');
    messageDiv.style.cssText = `
          margin-left: 55px;`;
    messageDiv.textContent = 'You are about to be logged out due to inactivity. Please confirm if you want to continue?';
    toast.appendChild(messageDiv);

    const buttonRow = document.createElement('div');
    buttonRow.style.display = 'flex';
    buttonRow.style.justifyContent = 'center';
    buttonRow.style.marginTop = '16px';

    const continueBtn = document.createElement('button');
    continueBtn.className = 'rect';
    continueBtn.style.cssText = `
          color: white;
          background-color: #009e73;
          border-radius: 4px;
          border: none;
          padding: 8px 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          margin: 0 12px;
        `;
    const continueContent = document.createElement('div');
    continueContent.style.display = 'flex';
    continueContent.style.alignItems = 'center';
    const continueIcon = document.createElement('img');
    continueIcon.src = '/assets/images/accept.png';
    continueIcon.style.marginRight = '4px';
    continueContent.appendChild(continueIcon);
    continueContent.appendChild(document.createTextNode('Continue'));
    continueBtn.appendChild(continueContent);

    continueBtn.addEventListener('click', () => {
      modal.remove();
      if (this.warningTimer) clearTimeout(this.warningTimer);
      this.resetTimer();
    });

    const logoutBtn = document.createElement('button');
    logoutBtn.className = 'rect green-btn';
    logoutBtn.style.cssText = `
          color: white;
          background-color: #9f0f0f;
          border-radius: 4px;
          border: none;
          padding: 8px 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          margin: 0 12px;
        `;
    const logoutContent = document.createElement('div');
    logoutContent.style.display = 'flex';
    logoutContent.style.alignItems = 'center';
    const logoutIcon = document.createElement('img');
    logoutIcon.src = '/assets/images/reject.png';
    logoutIcon.style.marginRight = '4px';
    logoutContent.appendChild(logoutIcon);
    logoutContent.appendChild(document.createTextNode('Logout'));
    logoutBtn.appendChild(logoutContent);

    logoutBtn.addEventListener('click', async () => {
      modal.remove();
      this.stopTimer();
      Common.logout(this.router);
    });

    buttonRow.appendChild(continueBtn);
    buttonRow.appendChild(logoutBtn);
    toast.appendChild(buttonRow);

    const modal = document.createElement('div');
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.width = '100%';
    modal.style.height = '100%';
    modal.style.backgroundColor = 'rgba(0,0,0,0.5)';
    modal.style.display = 'flex';
    modal.style.justifyContent = 'center';
    modal.style.alignItems = 'center';
    modal.style.zIndex = '10000';

    modal.appendChild(toast);
    document.body.appendChild(modal);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopTimer();
  }
}
